import React from 'react';
import { ExternalLink, CheckCircle2, FileText, DollarSign, Link2 } from 'lucide-react';
import { Section, Card, IconTile } from '../../hospital/shared/primitives';

const ACCENT = '#047857';

const FIRST_STEPS = [
  ['Identify a clinical question', 'A gap in current practice, or something that could be done better.'],
  ['Find a faculty mentor', 'Someone with research experience in the area of interest.'],
  ['Complete CITI training', 'Required ethics training for everyone on a protocol.'],
  ['Refine the question', 'With the mentor, until it is specific and answerable.'],
  ['Choose the study type', 'Quality improvement, case report, retrospective or prospective.'],
  ['Consult a statistician early', 'Study design and power calculation before data collection begins.'],
  ['Prepare the IRB submission', 'Confirm whether review is required and assemble the documents.'],
];

const BCM_RESOURCES = [
  ['Office of Clinical Research', 'Support for clinical trials and research studies', 'https://www.bcm.edu/research/research-offices/office-of-clinical-research'],
  ['Biostatistics consultation', 'Statistical support through the Institute for Clinical and Translational Research', 'https://www.bcm.edu/research/research-offices/institute-for-clinical-and-translational-research/service-cores/biostatistics'],
  ['BRAIN, the IRB portal', 'Submit and track IRB applications. BCM login required', 'https://brain.bcm.edu/brainNew'],
  ['BRAIN training', 'Self-paced application training. BCM login required', 'https://intouch.bcm.edu/sites/research/SitePageModern/71245/brain-application-training'],
  ['CITI Program', 'Required research ethics training', 'https://about.citiprogram.org'],
];

const IRB_TIPS = [
  ['Start early', 'Initial review takes four to eight weeks.'],
  ['Be specific', 'Vague applications draw questions and delay approval.'],
  ['State the risks', 'Name each risk and how it will be mitigated.'],
  ['Include every study document', 'Consent forms, surveys, recruitment materials and data collection forms.'],
  ['Check for exemption', 'Quality improvement and some retrospective studies qualify for exempt or expedited review.'],
  ['Amend before changing', 'Any protocol change needs approval before it is implemented.'],
  ['Keep records', 'Document correspondence and maintain the regulatory binder.'],
];

const FUNDING = [
  ['FAER', 'Foundation for Anesthesia Education and Research. Grants for anesthesiology trainees and early-career faculty', 'https://www.faer.org'],
  ['ASA research funding', 'American Society of Anesthesiologists research programs', 'https://www.asahq.org/research-and-publications'],
  ['NIH career development awards', 'K series awards for physician-scientists', 'https://researchtraining.nih.gov/programs/career-development'],
  ['Departmental and BCM pilot funding', 'Contact the department research coordinator for current internal opportunities', null],
];

const LinkRow = ({ title, subtitle, href }) => {
  const inner = (
    <>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[15px] text-gray-900 dark:text-white">{title}</div>
        {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{subtitle}</div>}
      </div>
      {href && <ExternalLink size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />}
    </>
  );
  const cls = 'w-full flex items-center gap-3 px-4 py-3 text-left';
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={`${cls} hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors`}>{inner}</a>
    : <div className={cls}>{inner}</div>;
};

const NumberedRows = ({ items }) => (
  <ol>
    {items.map(([title, text], i) => (
      <li key={i} className="flex items-start gap-3 px-4 py-3">
        <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${ACCENT}1f`, color: ACCENT }}>{i + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[15px] text-gray-900 dark:text-white">{title}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{text}</div>
        </div>
      </li>
    ))}
  </ol>
);

const Intro = ({ icon, title, children }) => (
  <Card>
    <div className="px-4 py-3 flex items-start gap-3">
      <IconTile icon={icon} accent={ACCENT} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[15px] text-gray-900 dark:text-white">{title}</div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-snug">{children}</p>
      </div>
    </div>
  </Card>
);

const GettingStartedTab = () => (
  <div className="space-y-6">
    <Intro icon={CheckCircle2} title="Research in residency">
      Case reports, quality improvement and retrospective studies are all within reach during training. The sequence below is the same for each; the mentor and the statistician are the two people to find first.
    </Intro>

    <Section title="First steps">
      <Card><NumberedRows items={FIRST_STEPS} /></Card>
    </Section>

    <Section title="BCM research resources">
      <Card>{BCM_RESOURCES.map(([t, s, h]) => <LinkRow key={t} title={t} subtitle={s} href={h} />)}</Card>
    </Section>

    <Section title="IRB submission">
      <Card>
        <div className="px-4 py-3 flex items-center gap-3"><IconTile icon={FileText} accent={ACCENT} /><div className="text-sm text-gray-600 dark:text-gray-400">Seven points that save weeks.</div></div>
        <NumberedRows items={IRB_TIPS} />
      </Card>
    </Section>

    <Section title="Funding">
      <Card>
        <div className="px-4 py-3 flex items-center gap-3"><IconTile icon={DollarSign} accent={ACCENT} /><div className="text-sm text-gray-600 dark:text-gray-400">Sources that fund trainee and early-career work.</div></div>
        {FUNDING.map(([t, s, h]) => <LinkRow key={t} title={t} subtitle={s} href={h} />)}
      </Card>
    </Section>

    <p className="px-1 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5"><Link2 size={12} /> External links open in the browser.</p>
  </div>
);

export default GettingStartedTab;
